import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  merge,
  shareReplay,
  type Observable,
} from 'rxjs';
import { ViewIds } from '@ardoq/api-types';
import { shallowEqual } from 'utils/objectUtils';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import { toViewpointModeMainViewInstanceId } from 'appLayout/mainViewInstanceIds';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { warmConditionalFormattingFilterCache } from 'tabview/bubbleChart/sourceData/buildViewpointModeViewModelSourceData';
import { buildContourSourceData } from '../sourceData/buildContourSourceData';
import type { ContourViewProps } from '../ContourView';
import {
  toPlottedViewEffects$,
  toStableSelection$,
} from 'tabview/chartShared/plottedViewEffects';
import { XYZ_DERIVATION_INPUTS, toXyzFieldSelection } from './xyzDerivation';
import { colors } from '@ardoq/design-tokens';
import { toUnknownPaintCss } from '@ardoq/xyz-charts';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { toFieldColoringLegendRows } from '../fieldColoringLegend';

const VIEW_ID = ViewIds.CONTOUR;
const VIEW_INSTANCE_ID = toViewpointModeMainViewInstanceId(VIEW_ID);

/* The settings stream carries the current settings alongside the keys that just changed; only
   the settings themselves are wanted here. */
const viewSettings$ = getViewpointModeViewSettingsWithChanges$(
  VIEW_ID,
  VIEW_INSTANCE_ID
).pipe(map(({ currentSettings }) => currentSettings));

const sourceData$ = combineLatest({
  ...XYZ_DERIVATION_INPUTS,
  /* Narrowed, so a ramp or a band count cannot re-derive every point. This chart does no
     gridding of its own in the builder, so not even `interpolation` reaches it. */
  viewSettings: toStableSelection$(viewSettings$, toXyzFieldSelection),
}).pipe(
  debounceTime(DEFAULT_DEBOUNCE_TIME),
  distinctUntilChanged(shallowEqual),
  map(args =>
    buildContourSourceData(VIEW_INSTANCE_ID, {
      ...args,
      /* Warmed in the same step that reads it, rather than as its own entry above. `filterCache`
         is a mutable singleton, and the debounce between `combineLatest` and here is a window in
         which another view could warm it with its own data. */
      filterColorData: warmConditionalFormattingFilterCache(args.loadedGraph),
    })
  ),
  /* Three subscribers - the props branch and the two effects - and the derivation is a pass over
     every component in the traversal. Without this it runs once per subscriber. */
  shareReplay({ bufferSize: 1, refCount: true })
);

/** Everything the 2D contour view is rendered from. */
export const contourViewModel$: Observable<ContourViewProps> = merge(
  /* The full settings on their own branch, so the view follows every settings write while the
     derivation above follows only the five field names. */
  combineLatest([sourceData$, viewSettings$]).pipe(
    map(([sourceData, viewSettings]) => ({
      ...sourceData,
      viewSettings,
      legendRows: toFieldColoringLegendRows(
        {
          coloring: sourceData.coloring,
          ...sourceData.legend,
          zAxisTitle: sourceData.valueTitle,
          ramp: viewSettings.ramp,
          bands: viewSettings.bands,
          unknownColor: toUnknownPaintCss(colors.surfaceDefault),
        },
        sourceData.i18nLocale
      ),
      viewLegendLabels: getViewLegendLabels(sourceData.i18nLocale),
    }))
  ),
  toPlottedViewEffects$(sourceData$, VIEW_ID)
);
